import React, { memo, useCallback, useEffect } from 'react';
import { useService } from 'react-service-locator';
import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';
import notification from 'antd/lib/notification';
import { UpdateAppModal } from 'src/components/domain/capacitor/components/update-app-modal';
import {
  ErrorBoundary,
  globalErrorNotificationKey,
} from 'src/components/error-boundary/error-boundary';
import { NotificationProvider } from 'src/components/general/feedback/hooks/use-show-notification';
import { useAuth } from 'src/components/providers/auth-provider';
import { AuthenticatedRouter } from 'src/components/routing/authenticated-router/authenticated-router';
import { UnauthenticatedRouter } from 'src/components/routing/unauthenticated-router';
import { useSetUpdateCapgoBundleOnBgListener } from 'src/hooks/capgo/use-set-update-capgo-bundle-on-bg-listener';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useHistoryChange } from 'src/hooks/use-history-change';
import { useI18n } from 'src/hooks/use-i18n';
import { useVisible } from 'src/hooks/use-visible';
import { AppStatusService } from 'src/services/app-status.service';
import { updateCapgoBundleImmediately } from 'src/utils/capacitor/app-updater';

const useNewBuildVersionUpdate = () => {
  const updateAppModalVisibility = useVisible();
  const { getFeatureFlag } = useAuthorization();
  const {
    state: { newVersionAvailable },
    currentBuild,
  } = useService(AppStatusService);

  //Force refresh if the client build date is older than the minimum required
  const minimumClientBuildDateRequired = getFeatureFlag(
    'config-minimum-client-build-date-required',
  );

  useEffect(() => {
    void (async () => {
      if (!newVersionAvailable || !minimumClientBuildDateRequired) {
        return;
      }

      if (new Date(currentBuild.date) < new Date(minimumClientBuildDateRequired)) {
        if (Capacitor.isNativePlatform()) {
          const didUpdate = await updateCapgoBundleImmediately();

          if (didUpdate) {
            return;
          }

          const result = await AppUpdate.getAppUpdateInfo();

          if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
            updateAppModalVisibility.show();
            return;
          }
        } else {
          window.location.reload();
        }
      }
    })();
  }, [
    newVersionAvailable,
    minimumClientBuildDateRequired,
    currentBuild,
    updateAppModalVisibility.show,
    updateAppModalVisibility,
  ]);

  const refreshPageOnNewVersion = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    if (newVersionAvailable) {
      window.location.reload();
    }
  }, [newVersionAvailable]);

  useHistoryChange(refreshPageOnNewVersion);

  return {
    showUpdateAppModal: updateAppModalVisibility.visible,
  };
};

export const Landing = memo(() => {
  const { user } = useAuth(false);

  useI18n(user?.language);
  const { showUpdateAppModal } = useNewBuildVersionUpdate();

  const closeGlobalErrorNotification = useCallback(() => {
    notification.close(globalErrorNotificationKey);
  }, []);

  useHistoryChange(closeGlobalErrorNotification);
  useSetUpdateCapgoBundleOnBgListener();

  return (
    <ErrorBoundary>
      <NotificationProvider>
        {user ? <AuthenticatedRouter /> : <UnauthenticatedRouter />}
      </NotificationProvider>
      {showUpdateAppModal && <UpdateAppModal />}
    </ErrorBoundary>
  );
});
